import * as React from "react";

import { translate } from "@/modules/shared/IntlProviderWrapper";
import { ORDER_TYPE } from "@/lib/ManufacturingConsts.generated";
import { classNames } from "@/lib/util";

import "./OrderBadges.scss";

export const ORDER_BADGE_TYPES = {
    plan: "plan",
    chunk: "chunk",
    unreleased: "unreleased",
    priority: "priority",
    tool_setup: "tool_setup",
    collective_order: "collective_order",
};

type Props = {
    className?: string;
    order_type?: string;
    priority?: string;
    is_unreleased?: boolean;
    has_tool_setup?: boolean;
    order_id?: string;
    leading_order_id?: string;
    onClick?: (event: React.MouseEvent<HTMLSpanElement>) => Promise<void>;
};

const OrderBadges = (props: Props) => {
    const { className, order_type, is_unreleased, priority, has_tool_setup, order_id, leading_order_id } = props;
    const badges: React.ReactNode[] = [];

    if (order_type === ORDER_TYPE.plan) {
        badges.push(
            <span
                key={ORDER_BADGE_TYPES.plan}
                className="badge badge-dark"
                title={translate("order_type.plan", "Plan")}
            >
                P
            </span>
        );
    }

    if (order_type === ORDER_TYPE.chunk) {
        badges.push(
            <span
                key={ORDER_BADGE_TYPES.chunk}
                className="badge badge-dark"
                title={translate("order_type.chunk", "Chunk")}
            >
                C
            </span>
        );
    }

    if (is_unreleased && order_type !== ORDER_TYPE.plan) {
        badges.push(
            <span
                key={ORDER_BADGE_TYPES.unreleased}
                className="badge badge-dark"
                title={translate("common.unreleased", "Unreleased")}
            >
                U
            </span>
        );
    }

    if (priority !== undefined) {
        badges.push(
            <span
                key={ORDER_BADGE_TYPES.priority}
                className={classNames("badge badge-dark", `badge-priority-${priority.toLowerCase()}`)}
                title={translate("common.priority", "Priority")}
            >
                {priority}
            </span>
        );
    }

    if (has_tool_setup) {
        badges.push(
            <span
                key={ORDER_BADGE_TYPES.tool_setup}
                className="badge badge-dark"
                title={translate("common.tool_setup", "Tool Setup")}
            >
                <i className="fas fa-wrench" />
            </span>
        );
    }

    if (leading_order_id !== undefined && leading_order_id !== "") {
        const is_leading_order = leading_order_id === order_id;
        const badge_class = is_leading_order ? "badge-primary" : "badge-secondary";
        const label = is_leading_order ? "CL" : "CM";
        const title = is_leading_order
            ? translate("common.leading_order_of_collective_order_group", "Leading order of collective order group")
            : translate("common.member_of_collective_order_group", "Member of collective order group") +
              "\n • " +
              translate("common.leading_order", "Leading order") +
              ": " +
              leading_order_id;
        badges.push(
            <span
                key={ORDER_BADGE_TYPES.collective_order}
                data-id={ORDER_BADGE_TYPES.collective_order}
                className={classNames("badge", "badge-btn", badge_class)}
                title={title}
                onClick={props.onClick}
            >
                {label}
            </span>
        );
    }

    if (badges.length === 0) {
        return null;
    }

    return (
        <div
            className={classNames("order-badges", {
                [String(className)]: className !== undefined && className.trim() !== "",
            })}
        >
            {badges}
        </div>
    );
};

export default OrderBadges;
